import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PaperPlaneRight, TreeStructure } from 'phosphor-react';

export function MockChatInput() {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [selectedAgent] = useState('Agente Builder');

  const mockMessages = [
    'Crie um app de aprovação de despesas com tabela, filtros e status',
    'Gere um dashboard de vendas com métricas por região e período',
    'Conecte ao PostgreSQL e crie CRUD de clientes com permissões por papel',
    'Monte um workflow de onboarding com checklist e notificações no Slack',
    'Crie um app de tickets com SLA, prioridade e trilha de auditoria',
  ];

  const getAgentIconKey = (name: string): 'pen' | 'image' | 'search' | 'doc' | 'chart' | 'code' => {
    const n = (name || '').toLowerCase();
    if (n.includes('prd') || n.includes('montfort')) return 'pen';
    if (n.includes('técnico') || n.includes('otto') || n.includes('code') || n.includes('dev')) return 'code';
    if (n.includes('proposta') || n.includes('roussillon') || n.includes('document')) return 'doc';
    if (n.includes('relatório') || n.includes('gerier') || n.includes('chart') || n.includes('métric') || n.includes('metric')) return 'chart';
    if (n.includes('samson') || n.includes('search')) return 'search';
    if (n.includes('engelier') || n.includes('image') || n.includes('design')) return 'image';
    return 'pen';
  };

  useEffect(() => {
    const message = mockMessages[currentMessageIndex];
    let charIndex = 0;
    setIsTyping(true);
    setDisplayedText('');

    const typingInterval = setInterval(() => {
      if (charIndex < message.length) {
        setDisplayedText(message.slice(0, charIndex + 1));
        charIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
        
        // Wait a bit before switching to next message
        setTimeout(() => {
          setCurrentMessageIndex((prev) => (prev + 1) % mockMessages.length);
        }, 3000);
      }
    }, 80);

    return () => clearInterval(typingInterval);
  }, [currentMessageIndex]);

  return (
    <div className="relative w-full mx-auto">
      <div className="relative flex flex-col gap-2 p-2 rounded-2xl border transition-all theme-input">
        <div className="relative flex-1 min-h-[44px]">
          <div className="min-h-[44px] max-h-[200px] overflow-auto px-2 py-2 text-sm focus:outline-none theme-text w-full relative z-[1]">
            <span className="theme-text">{displayedText}</span>
            {isTyping && (
              <span className="theme-text-muted animate-pulse ml-0.5">|</span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between flex-row gap-2">
          <div className="flex flex-row gap-2 items-center">
            {/* Attachment button */}
            <motion.button
              type="button"
              disabled
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Anexar arquivos"
            >
              <svg className="inline-flex size-4 transition-colors fill-zinc-500" width="20" height="20" viewBox="0 0 20 20">
                <path d="M15.037 2.46a4.85 4.85 0 0 1 0 6.855l-.032.033a4.85 4.85 0 0 1-.801 5.801l-2.388 2.388a4.85 4.85 0 0 1-6.855 0 4.85 4.85 0 0 1 0-6.855l.032-.033a4.85 4.85 0 0 1 .801-5.801L8.181 2.46a4.85 4.85 0 0 1 6.855 0zm-5.971.884L6.677 5.732a3.6 3.6 0 0 0 0 5.087 3.6 3.6 0 0 0 5.087 0l.172-.172c.244-.244.64-.244.884 0s.244.64 0 .884l-.172.172a4.85 4.85 0 0 1-6.855 0l-.041-.042-.053.058a3.6 3.6 0 0 0 5.233 4.934l2.389-2.389a3.6 3.6 0 0 0 0-5.087 3.6 3.6 0 0 0-5.087 0l-.172.172c-.244.244-.64.244-.884 0s-.244-.64 0-.884l.172-.172a4.85 4.85 0 0 1 6.855 0l.039.042.055-.058a3.6 3.6 0 0 0-.145-4.934 3.6 3.6 0 0 0-5.087 0z"></path>
              </svg>
            </motion.button>

            {/* Agent dropdown */}
            <div className="relative">
              <button
                type="button"
                disabled
                className={[
                  'h-8 max-w-[240px]',
                  'inline-flex items-center gap-2 px-2 rounded-xl border',
                  'theme-border theme-card theme-text',
                  'transition-colors',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                ].join(' ')}
                title="Seleciona o agente para o chat"
              >
                <span className="w-5 h-5 rounded-xl p-1 bg-zinc-700/50 flex items-center justify-center">
                  <img
                    src={`/icons/feature/${getAgentIconKey(selectedAgent)}.svg`}
                    alt=""
                    width={24}
                    height={24}
                    className="block"
                  />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-xs font-medium truncate">
                    {selectedAgent}
                  </span>
                </span>
                <svg
                  className="w-4 h-4 text-zinc-500 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Tool dropdown (Integrations) */}
            <div className="relative">
              <button
                type="button"
                disabled
                className={[
                  'h-8 max-w-[240px]',
                  'inline-flex items-center gap-2 px-2 rounded-xl border',
                  'theme-border theme-card theme-text',
                  'transition-colors',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                ].join(' ')}
                title="Seleciona a integração (Tool)"
              >
                <span className="w-5 h-5 rounded-xl p-1 bg-zinc-700/50 flex items-center justify-center">
                  <TreeStructure className="w-4 h-4 text-zinc-400" />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-xs font-medium truncate">
                    Selecione a integração
                  </span>
                </span>
                <svg
                  className="w-4 h-4 text-zinc-500 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.8}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </div>

          <button
            type="button"
            disabled
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl border theme-border theme-card disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <PaperPlaneRight className="w-4 h-4 text-zinc-400" weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
